import styled from 'styled-components';

import { Text } from '@/nova/components';
import { CFG, CFG_MONO_FONT } from '../../../../../theme/configurator-tokens';
import { MOBILE, MOBILE_ANIM } from '../../mobile.tokens';
import { hiddenScrollbar , cardHover } from '../../../../../theme/configurator-keyframes';

export const Screen = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  background: ${MOBILE.screenBg};
`;

export const Scroll = styled.div`
  ${hiddenScrollbar};
  position: absolute;
  inset: 0;
  overflow-y: auto;
  padding: 72px 13px 150px;
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 20px 16px 0;
  text-align: center;
`;

export const HeadingTitle = styled(Text)`
  font-size: 18px;
  font-weight: 900;
  line-height: 1.2;
  color: ${CFG.brandText};
`;

export const HeadingSub = styled(Text)`
  font-size: 14px;
  line-height: 16px;
  color: ${CFG.base400};
`;

export const Summary = styled.div`
  margin-top: 28px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SummaryHead = styled(Text)`
  padding: 0 8px;
  font-size: 14px;
  line-height: 1;
  color: ${CFG.base400};
`;

export const Rule = styled.div`
  height: 1px;
  width: 100%;
  background: ${MOBILE.hairline};
`;

export const TermsButton = styled.button`
  margin-top: 8px;
  display: block;
  width: 100%;
  cursor: pointer;
  /* Reset UA-default button padding/line-height (Tailwind preflight does this in
     the reference) so the link sits tight under the summary, not ~12px lower. */
  padding: 0 8px 0 0;
  line-height: 1;
  font: inherit;
  text-align: right;
  background: none;
  border: none;
`;

export const TermsLabel = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
  color: ${MOBILE.whiteFaint};
  text-decoration: underline;
`;

/* ---- Billing row ---- */

export const RowEl = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  padding: 10px 8px;
`;

export const RowLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const RowLabel = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  color: ${CFG.base300};
`;

export const RowSub = styled(Text)<{ $color: string }>`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.2;
  color: ${({ $color }) => $color};
`;

/** A code-like value shown as a subtitle (the voucher code): a slightly-rounded monospaced pill. */
export const CodePill = styled(Text)`
  align-self: flex-start;
  font-family: ${CFG_MONO_FONT};
  font-size: 13px;
  line-height: 1.5;
  letter-spacing: 0.2px;
  padding: 1px 8px;
  border-radius: 6px;
  background: ${CFG.washHover};
  color: ${CFG.base400};
`;

export const RowRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  white-space: nowrap;
  color: ${MOBILE.white};
`;

export const RowPrice = styled.span<{ $color?: string }>`
  display: flex;
  gap: 2px;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  ${({ $color }) => ($color ? `color: ${$color};` : '')}
`;

export const RowNote = styled(Text)`
  font-size: 14px;
  line-height: 1.1;
  opacity: 0.5;
`;

/* ---- Sticky bottom bar ---- */

export const BottomBar = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  z-index: 20;
  padding: 36px 10px 20px;
  background-image: ${MOBILE.bottomFadeHeavy};
`;

export const TotalRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0 12px 14px;
`;

export const TotalLabels = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TotalLabel = styled(Text)`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.2;
  color: ${MOBILE.white};
`;

export const TotalSub = styled(Text)`
  font-size: 14px;
  color: ${CFG.base500};
`;

export const TotalAmount = styled.span`
  white-space: nowrap;
  font-size: 20px;
  font-weight: 700;
  color: ${MOBILE.white};
`;

export const Cta = styled.button`
  display: flex;
  height: 48px;
  width: 100%;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: none;
  background: ${CFG.brand};
  color: ${MOBILE.knockout};
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  /* Cursor-following hover glow + lit hairline ring (shared with the CTA Button). */
  ${cardHover}
`;

/* ---- Payment sheet ---- */

export const PaymentScrim = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 30;
  transition: opacity ${MOBILE_ANIM.previewFadeMs}ms ease;
  opacity: ${({ $open }) => ($open ? 1 : 0)};
  pointer-events: ${({ $open }) => ($open ? 'auto' : 'none')};
  background: ${MOBILE.paymentScrim};
`;

export const PaymentSheet = styled.div<{ $open: boolean }>`
  position: absolute;
  inset: 54px 0 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 32px 32px 0 0;
  background: ${MOBILE.panelBg};
  transition: transform ${MOBILE_ANIM.detailSlideMs}ms ease-out;
  transform: ${({ $open }) => ($open ? 'translateY(0)' : 'translateY(100%)')};
  padding-top: env(safe-area-inset-top, 0px);
`;

export const PaymentHeader = styled.div`
  position: relative;
  display: flex;
  height: 56px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid ${CFG.base100};
`;

export const PaymentTitle = styled(Text)`
  font-size: 16px;
  font-weight: 900;
  /* Gradient text clips to the glyph box, so keep enough leading for descenders. */
  line-height: 1.25;
  background-image: ${MOBILE.titleGradient};
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
`;

export const PaymentClose = styled.button`
  position: absolute;
  right: 16px;
  display: flex;
  width: 32px;
  height: 32px;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: none;
  background: ${MOBILE.hairline};
  color: ${MOBILE.white};
`;

export const PaymentBody = styled.div`
  min-height: 0;
  flex: 1;
  padding-top: 12px;
`;
